/**
 * Frontend JavaScript for MR Tabs Block
 * Handles tab switching, keyboard navigation, and accessibility
 */

(function () {
	"use strict";

	/**
	 * Initialize tabs functionality
	 */
	function initTabs() {
		// Find all tab containers on the page
		const tabContainers = document.querySelectorAll(".wp-block-mr-blocks-tabs");

		tabContainers.forEach((container) => {
			const tabItems = Array.from(
				container.querySelectorAll(
					":scope > .mr-tabs__content > .wp-block-mr-blocks-tabs-item",
				),
			);

			if (tabItems.length === 0) {
				return;
			}

			// Check if tabs are vertical
			const isVertical = container.classList.contains("mr-tabs--vertical");

			// Create tab navigation header
			const tabHeader = document.createElement("div");
			tabHeader.classList.add("mr-tabs__header");
			container.prepend(tabHeader);

			let hasDefaultOpen = false;
			const tabButtons = [];

			// Create tab buttons and set up initial state
			tabItems.forEach((tabItem, index) => {
				const content = tabItem.querySelector(
					":scope > .mr-tabs-item__content",
				);
				const header = tabItem.querySelector(":scope > .mr-tabs-item__header");
				const title = header ? header.textContent.trim() : `Tab ${index + 1}`;

				// Set up ARIA attributes
				content.setAttribute("role", "tabpanel");
				content.setAttribute("id", `tabpanel-${index}`);
				content.setAttribute("aria-labelledby", `tab-${index}`);

				// Create tab button
				const tabButton = document.createElement("button");
				tabButton.classList.add("mr-tabs__tab");
				tabButton.setAttribute("role", "tab");
				tabButton.setAttribute("id", `tab-${index}`);
				tabButton.setAttribute("aria-controls", `tabpanel-${index}`);
				tabButton.setAttribute("aria-selected", "false");
				tabButton.tabIndex = -1;
				tabButton.textContent = title;

				// Check if this tab should be default open
				const isDefaultOpen = tabItem.dataset.defaultOpen === "true";
				if (isDefaultOpen && !hasDefaultOpen) {
					tabButton.classList.add("mr-tabs__tab--active");
					tabButton.setAttribute("aria-selected", "true");
					tabButton.tabIndex = 0;
					content.classList.add("mr-tabs-item__content--active");
					header.classList.add("mr-tabs-item__header--active");
					hasDefaultOpen = true;
				} else {
					content.classList.add("mr-tabs-item__content--hidden");
					header.classList.add("mr-tabs-item__header--hidden");
				}

				// Add click event listener
				tabButton.addEventListener("click", () => {
					activateTab(tabItems, tabButtons, index);
				});

				// Add keyboard event listener
				tabButton.addEventListener("keydown", (event) => {
					handleKeyboardNavigation(
						event,
						tabButtons,
						tabItems,
						index,
						isVertical,
					);
				});

				tabButtons.push(tabButton);
				tabHeader.appendChild(tabButton);

				// Hide the original header
				if (header) {
					header.style.display = "none";
				}
			});

			// If no default tab is set, activate the first one
			if (!hasDefaultOpen && tabButtons.length > 0) {
				activateTab(tabItems, tabButtons, 0);
			}
		});
	}

	/**
	 * Activate a specific tab
	 * @param {Array} tabItems - Array of tab item elements
	 * @param {Array} tabButtons - Array of tab button elements
	 * @param {number} activeIndex - Index of tab to activate
	 */
	function activateTab(tabItems, tabButtons, activeIndex) {
		tabItems.forEach((tabItem, index) => {
			const content = tabItem.querySelector(":scope > .mr-tabs-item__content");
			const header = tabItem.querySelector(":scope > .mr-tabs-item__header");
			const button = tabButtons[index];

			const isActive = index === activeIndex;

			// Update content visibility
			content.classList.toggle("mr-tabs-item__content--active", isActive);
			content.classList.toggle("mr-tabs-item__content--hidden", !isActive);

			// Update header visibility
			if (header) {
				header.classList.toggle("mr-tabs-item__header--active", isActive);
				header.classList.toggle("mr-tabs-item__header--hidden", !isActive);
			}

			// Update button state
			button.classList.toggle("mr-tabs__tab--active", isActive);
			button.setAttribute("aria-selected", isActive);
			button.tabIndex = isActive ? 0 : -1;
		});

		// Focus the active tab button
		tabButtons[activeIndex].focus();

		// Scroll active tab into view if needed
		scrollTabIntoView(tabButtons[activeIndex]);
	}

	/**
	 * Handle keyboard navigation for tabs
	 * @param {Event} event - Keyboard event
	 * @param {Array} tabButtons - Array of tab button elements
	 * @param {Array} tabItems - Array of tab item elements
	 * @param {number} currentIndex - Current tab index
	 * @param {boolean} isVertical - Whether tabs are in vertical orientation
	 */
	function handleKeyboardNavigation(
		event,
		tabButtons,
		tabItems,
		currentIndex,
		isVertical = false,
	) {
		let newIndex = currentIndex;

		switch (event.key) {
			case "ArrowLeft":
				if (!isVertical) {
					event.preventDefault();
					newIndex =
						currentIndex > 0 ? currentIndex - 1 : tabButtons.length - 1;
				}
				break;
			case "ArrowRight":
				if (!isVertical) {
					event.preventDefault();
					newIndex =
						currentIndex < tabButtons.length - 1 ? currentIndex + 1 : 0;
				}
				break;
			case "ArrowUp":
				if (isVertical) {
					event.preventDefault();
					newIndex =
						currentIndex > 0 ? currentIndex - 1 : tabButtons.length - 1;
				}
				break;
			case "ArrowDown":
				if (isVertical) {
					event.preventDefault();
					newIndex =
						currentIndex < tabButtons.length - 1 ? currentIndex + 1 : 0;
				}
				break;
			case "Home":
				event.preventDefault();
				newIndex = 0;
				break;
			case "End":
				event.preventDefault();
				newIndex = tabButtons.length - 1;
				break;
			case "Enter":
			case " ":
				event.preventDefault();
				activateTab(tabItems, tabButtons, currentIndex);
				return;
			default:
				return;
		}

		if (newIndex !== currentIndex) {
			activateTab(tabItems, tabButtons, newIndex);
		}
	}

	/**
	 * Scroll tab button into view if needed
	 * @param {Element} tabButton - Tab button element
	 */
	function scrollTabIntoView(tabButton) {
		if (!tabButton) {
			return;
		}

		const rect = tabButton.getBoundingClientRect();
		const isInView =
			rect.top >= 0 &&
			rect.left >= 0 &&
			rect.bottom <=
				(window.innerHeight || document.documentElement.clientHeight) &&
			rect.right <= (window.innerWidth || document.documentElement.clientWidth);

		if (!isInView) {
			tabButton.scrollIntoView({ behavior: "smooth", block: "nearest" });
		}
	}

	/**
	 * Initialize when DOM is ready
	 */
	if (typeof document !== "undefined") {
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", initTabs);
		} else {
			initTabs();
		}
	}
})();
